import { FieldPath, PathValue, UseFormReturn } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Callout } from '__types__/v1/models/Callout';
import {
  createCalloutSlugAvailabilityValidator,
  validateCalloutSlugFormat
} from 'v5/features/callouts/callout-slug-field.validation';
import { titleToCalloutSlug } from 'v5/api/callouts/callout-slug.logic';
import { useCalloutPublicUrlPrefix } from 'v5/features/callouts/useCalloutPublicUrlPrefix';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';

export type CalloutSlugFormValues = {
  title: string;
  slug: string;
};

type UseCalloutSlugFieldConfig<T extends CalloutSlugFormValues> = {
  form: UseFormReturn<T>;
  syncSlugFromTitle?: boolean;
  excludeCalloutId?: Callout['id'];
  workspaceId?: number | null;
} & WithServices<
  [WithCalloutsService, WithAppScopeService, WithEnterpriseService]
>;

export function useCalloutSlugField<T extends CalloutSlugFormValues>({
  form,
  syncSlugFromTitle = true,
  excludeCalloutId,
  workspaceId,
  services
}: UseCalloutSlugFieldConfig<T>) {
  const { t } = useTranslation('callouts');
  const slugPrefix = useCalloutPublicUrlPrefix(services, workspaceId);
  const slugPath = 'slug' as FieldPath<T>;
  const titlePath = 'title' as FieldPath<T>;
  const title = form.watch(titlePath);

  const [isSyncFromTitleActive, setIsSyncFromTitleActive] =
    useState(syncSlugFromTitle);

  const validateAvailability = useMemo(
    () =>
      createCalloutSlugAvailabilityValidator({
        form,
        slugPath,
        calloutsService: services.calloutsService,
        excludeCalloutId,
        workspaceId,
        t
      }),
    [excludeCalloutId, form, services.calloutsService, slugPath, t, workspaceId]
  );

  const validationRules = useMemo(
    () => ({
      validate: {
        format: (value: string) =>
          validateCalloutSlugFormat(
            value,
            String(form.getValues(titlePath) ?? ''),
            t
          ),
        availability: validateAvailability
      }
    }),
    [form, titlePath, t, validateAvailability]
  );

  const writeSlug = useCallback(
    (nextSlug: string, options?: { shouldDirty?: boolean }) => {
      form.setValue(slugPath, nextSlug as PathValue<T, FieldPath<T>>, {
        shouldDirty: options?.shouldDirty ?? false,
        shouldValidate: true
      });
    },
    [form, slugPath]
  );

  const applySyncedSlug = useCallback(
    (options?: { shouldDirty?: boolean }) => {
      const syncedSlug = titleToCalloutSlug(String(title ?? '')) || 'callout';
      writeSlug(syncedSlug, options);
    },
    [title, writeSlug]
  );

  const enableSyncFromTitle = useCallback(
    (options?: { shouldDirty?: boolean }) => {
      setIsSyncFromTitleActive(true);
      applySyncedSlug(options);
    },
    [applySyncedSlug]
  );

  const handleSlugInputChange = useCallback(
    (rawValue: string) => {
      const nextSlug = rawValue.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setIsSyncFromTitleActive(false);
      writeSlug(nextSlug, { shouldDirty: true });
    },
    [writeSlug]
  );

  useEffect(() => {
    setIsSyncFromTitleActive(syncSlugFromTitle);
  }, [syncSlugFromTitle]);

  useEffect(() => {
    if (!isSyncFromTitleActive) {
      return;
    }

    const syncedSlug = titleToCalloutSlug(String(title ?? ''));
    if (form.getValues(slugPath) !== syncedSlug) {
      writeSlug(syncedSlug);
    }
  }, [form, isSyncFromTitleActive, slugPath, title, writeSlug]);

  return {
    slugPath,
    slugPrefix,
    validationRules,
    showSyncFromTitleAction: !isSyncFromTitleActive,
    handleSlugInputChange,
    enableSyncFromTitle
  };
}
